import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { StateService } from '@uirouter/core';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { IdentityService } from 'account-hybrid/core/authentication';
import { GuestsApiService, GuestsQuery } from '../services/guests-api.service';

interface DirectoryFilter {
  name: string;
  query: GuestsQuery;
}

/**
 * Native-Angular port of the AngularJS people DirectoryController
 * (account-v1/.../people/directory/directory.controller.js) + directory.html.
 * Replaces the `people.directory` UIRouter state's AngularJS component.
 */
@Component({
    selector: 'ft-people-directory-ng',
    templateUrl: './directory.component.html',
    standalone: false
})
export class DirectoryComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('scrollSentinel') scrollSentinel?: ElementRef<HTMLElement>;

  readonly filters: DirectoryFilter[] = [
    { name: 'Most recent', query: { SortBy: 'create_date', SortDirection: 'desc', Offset: 0, Fetch: 25 } },
    { name: 'Top advocates', query: { SortBy: '', SortDirection: 'desc', Offset: 0, Fetch: 25 } },
  ];
  filter: DirectoryFilter = this.clone(this.filters[0]);
  advocates: any[] = [];
  criteria = '';
  searchText = '';
  hasMoreData = false;
  isProcessingRequest = false;

  private queryToken = 0;
  private observer?: IntersectionObserver;

  constructor(
    private readonly guests: GuestsApiService,
    public readonly identity: IdentityService,
    private readonly spaces: SpacesService,
    private readonly stateService: StateService,
  ) {}

  ngOnInit(): void {
    this.changeFilter(this.filters[0]);
  }

  ngAfterViewInit(): void {
    if (!this.scrollSentinel) return;
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && this.hasMoreData && !this.isProcessingRequest) {
        this.loadNextPage();
      }
    });
    this.observer.observe(this.scrollSentinel.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  /** Total count comes back on the first advocate row (matches the v1 controller). */
  get total(): number {
    return this.advocates.length > 0 ? this.advocates[0].totalCount : 0;
  }

  changeFilter(newFilter: DirectoryFilter): void {
    this.filter = this.clone(newFilter);
    this.advocates = [];
    this.load((data) => {
      this.advocates = Array.isArray(data.advocates) ? data.advocates : [];
    });
  }

  setCriteria(criteria: string): void {
    this.searchText = criteria;
    this.filter.query.Offset = 0;
    this.advocates = [];
    this.load((data) => {
      this.advocates = Array.isArray(data.advocates) ? data.advocates : [];
    });
  }

  loadNextPage(): void {
    this.filter.query.Offset += this.filter.query.Fetch;
    this.load((data) => {
      this.advocates = this.advocates.concat(data.advocates ?? []);
    });
  }

  details(guest: any): void {
    this.stateService.go('people.directory.details', { advocateUUID: guest.campaignPromoterUUID });
  }

  private load(onData: (data: any) => void): void {
    const token = ++this.queryToken;
    this.isProcessingRequest = true;
    const query: GuestsQuery = { ...this.filter.query, SearchText: this.searchText };
    this.guests.guests(this.spaces.getPropertyUuid(), query).subscribe({
      next: (data) => {
        if (token !== this.queryToken) return;
        const total =
          Array.isArray(data.advocates) && data.advocates.length > 0 ? data.advocates[0].totalCount : 0;
        this.hasMoreData = total > this.filter.query.Offset + this.filter.query.Fetch;
        onData(data);
      },
      complete: () => {
        if (token === this.queryToken) this.isProcessingRequest = false;
      },
      error: () => {
        if (token === this.queryToken) this.isProcessingRequest = false;
      },
    });
  }

  private clone(f: DirectoryFilter): DirectoryFilter {
    return { name: f.name, query: { ...f.query } };
  }
}
